import {
  type ApiError,
  useAcquireControlLock,
  useBatchProgress,
  useBatchRunningModuleType,
  useClearSafety,
  useControlLock,
  useEmergencyStop,
  usePauseBatch,
  usePermissions,
  useRecoverBatch,
  useReleaseControlLock,
  useResumeBatch,
  useSafetyState,
  useStopBatch,
  useStops,
  useCurrentUser,
  useModules,
  useBatchTracking,
  useBatches,
  usePreflightSnapshot,
  useRecordReagentReplenishment,
  useStreamProgressSeed,
} from '@ctcells/api'
import type { PreflightResult } from '@ctcells/contract'
import { dataSourceFor, useStripChoiceStore } from '@ctcells/core'
import { errorMessage, serverText, t } from '@ctcells/i18n'
import { useControlDisabled, useStreamState } from '@ctcells/realtime'
import { useEffect, useMemo, useState } from 'react'

import { haltLine } from '../screens/batches/haltLine.ts'
import {
  refusedResumeChecks,
  timedAtomChoices as timedAtomChoicesOf,
  type ResumeCheck,
} from '../screens/batches/resumeRouteModel.ts'
import { batchTransferring } from '../screens/monitoring/chamberProgress.ts'
import { useMinuteTick } from '../screens/monitoring/minuteTick.ts'
import { useSimStartPrepare } from '../screens/batches/useSimStartPrepare.ts'

import { useActiveBatchSource } from './activeBatchSource.ts'
import { BatchStrip } from './BatchStrip.tsx'
import {
  stripBatchChoice,
  stripDisposition,
  stripLoadWait,
  stripModuleSteps,
  stripOwnerText,
  stripRecipeNames,
  toControlLockView,
  stripHaltAt,
  stripTransferRoutes,
  stripTrayHolds,
  toStripBatch,
} from './batchStripModel.ts'
import type {
  BatchCommand,
  BatchStripProps,
  BatchStripVariant,
  ControlLockAction,
} from './batchStripTypes.ts'
import { needsReagentConfirm, type ReagentBody } from './reagentConfirm.ts'
import { startGateKey } from './startGate.ts'
import {
  StripCommandDialog,
  type StripCommandAction,
  type StripConfirmKind,
} from './StripCommandDialog.tsx'
import { StripEvents, type StripEventsFilter } from './StripEvents.tsx'
import { useExplicitStripPick, useStripBatch } from './stripPick.ts'
import { isGlobalEstop, systemSafetyStopOf } from './estopMode.ts'
import { stripPreflightPhase } from './stripStages.ts'
import { StripReagentDialog } from './StripReagentDialog.tsx'
import { StripStartDialog } from './StripStartDialog.tsx'
import { useCancelAfterStop, useLeftoverExecutions } from './executionCancel.ts'
import { EMPTY_STEP_MEMORY, readStepMemory, rememberSteps } from './stripStepMemory.ts'
import { useStartBlockers } from './useStartBlockers.ts'
import { useStartGate } from './useStartGate.ts'

/**
 * 상시 작업 줄의 **데이터·명령 배선**. 그리는 일은 `BatchStrip` 이 하고 여기는 잇기만 한다.
 *
 * 🔴 **셸이 이것을 쓴다 — 화면이 아니다.** 콘솔 · 태블릿 · 장비 관점 콘솔 셋이 같은 것을 꽂는다.
 *    작업 줄은 라우트와 무관하게 계속 떠 있어야 해서 화면이 소유할 수 없다.
 *
 * 무엇을 묶는가
 *
 * | 축               | 출처                                                            |
 * | ---------------- | --------------------------------------------------------------- |
 * | 작업 · 허용 명령 | `GET /batches?phase=active` — `allowed_commands` **그대로**       |
 * | 레시피 이름      | `GET /recipes` (계약에 배치→이름이 없다)                          |
 * | 안전 상태        | `GET /safety`                                                    |
 * | 제어락           | 리스 보관소(`useControlLock`) — 조회 경로가 계약에 0건이다         |
 * | 권한             | 세션이 준 역할(`usePermissions`)                                  |
 * | 연결             | 구독 2채널 stale 판정(`useControlDisabled`)                        |
 * | 진행(3행)        | 구독 `batch_progress` 보관소(`useBatchProgress`) — REST 에 없다     |
 *
 * 🔴 **버튼 활성은 서버가 준 `allowed_commands` 그대로다.** 여기서 상태를 보고 더하거나 빼지
 *    않는다. 서버 `TRANSITIONS` 상 집합은 `start|stop|pause|resume|recover` 다섯뿐이고,
 *    `has_write_role` 이나 `holds_lock` 이 거짓이면 **서버가 빈 배열**을 준다 — 그러면 전부
 *    비활성 + 사유가 되는 것이 정상이다(`app/services/batches.py` `allowed_commands`).
 * 🔴 **낙관적 갱신이 없다.** 명령은 전부 `createCommand({send, invalidates})` 를 지나므로
 *    성공해도 하는 일은 무효화 하나다 — 화면은 서버가 다시 준 값으로 그려진다(DP-04).
 *    ⚠ `mutate(vars, { onSuccess })` 로 붙인 콜백은 **대화를 닫고 문구를 지우는 것뿐**이다.
 *      캐시를 손대지 않는다 — 손댈 통로가 `createCommand` 에 타입상 없다.
 * 🔴 **주기 폴링이 없다**(DP-01). 스냅샷 1회 + 구독(`useStreamBridge` 가 무효화한다)이다.
 *
 * 🔴 **진행은 구독에서만 온다 — 그리고 오는 것은 3행 한 줄뿐이다.** 계약·실물 전수(2026-09-20)
 *    결과 `batch_progress` 본문(`ExecutionStream` 동형)에 있는 것은 `step`·`total`·`message` 다.
 *    **진행률 · 경과 · 총 예상 · 편차 · 구간은 계약에도 실물에도 없어서 계속 비어 있다**
 *    (`docs/backend-requests.csv` BR-09). `step`/`total` 로 백분율을 만들지 않는다.
 * 🔴 **stale 이면 진행을 지운다.** 마지막 값을 「지금」인 척 두지 않는다 — 계약이 그것을 금한다
 *    (`events.asyncapi.yaml` `L1EventStream.stale`). 보관소도 셸이 함께 비운다
 *    (`useStreamProgressReset` · `AppRuntime`).
 * ⚠ **모듈 패드(`ModuleLayout`)는 이 배선을 타지 않는다.** 모듈 로컬 API 는 L2 제어락 축이
 *   아니고 `allowed_commands` 도 다른 값이다(CLAUDE.md) — 섞으면 안 된다.
 */

export interface BatchStripContainerProps {
  /** 셸이 쥔 이벤트 펼침 상태 */
  eventsOpen?: boolean | undefined
  onToggleEvents?: (() => void) | undefined
  /**
   * 펼친 이벤트 칸을 **채울지**(`StripEvents`). 통합 콘솔만 켠다(2026-09-28).
   * ⚠ 태블릿·장비 관점은 아직 안 켠다 — 그 셸들은 펼치면 본문을 숨기는 옛 배치라 켜기 전에
   *   레이아웃을 같이 봐야 한다.
   */
  eventsPanel?: boolean | undefined
  /** 이벤트 단추를 시작된 작업이 있을 때만 세운다 — 통합 콘솔만 켠다(`BatchStripProps.eventsOnlyStarted`) */
  eventsOnlyStarted?: boolean | undefined
  /** 이벤트 단추를 2행 요약 카드 안에 세운다 — 통합 콘솔만 켠다(`BatchStripProps.eventsInTally`) */
  eventsInTally?: boolean | undefined
  /** 2행에 준비 · 완료 영역을 끼운다 — 통합 콘솔만 켠다(`BatchStripProps.flowZones`) */
  flowZones?: boolean | undefined
  /** 드롭다운에 고를 작업이 없을 때의 [작업 추가] → 새 작업. 안 넘기면 그 단추가 없다 */
  onNewBatch?: (() => void) | undefined
  /** E-STOP 아랫줄 글자(「전체 비상 정지」) — 통합 콘솔만 끈다(`BatchStripProps.estopCaption`). 기본 켬 */
  estopCaption?: boolean | undefined
  /** 요약 카드에 「예상」 줄을 세운다 — 통합 콘솔만 켠다(`BatchStripProps.estimate` · 지금은 미수신 · BR-09) */
  tallyEstimate?: boolean | undefined
  /** 1행을 작업 이름 먼저 세운다 — 통합 콘솔만 켠다(`BatchStripProps.nameFirst`) */
  nameFirst?: boolean | undefined
  /**
   * 2행의 모습 — `full`(공정 칸 셋 + 요약) · `compact`(현재 공정 한 줄). 🔴 **셸이 경로로 고른다**(`stripVariantFor` · 태블릿).
   * 통합 콘솔은 안 넘긴다 — 어느 메뉴에서든 `full` 이다(eric 2026-10-03).
   * 컨테이너는 내용으로 바꾸지 않는다 — 그래야 화면 종류마다 높이가 고정이다. 기본 `full`.
   */
  variant?: BatchStripVariant | undefined
  /** 접힘 — 셸이 쥔 화면 배치 설정(`BatchStripProps.collapsed`). 통합 콘솔만 넘긴다 */
  collapsed?: boolean | undefined
  /** 접기/펼치기 단추. 안 넘기면 단추가 없다 */
  onToggleCollapsed?: (() => void) | undefined
  /*
   * 🔴 **이동은 셸이 쥔다 — 컨테이너가 경로를 모른다.** 셸마다 경로 표가 다르기 때문이다:
   *    콘솔·장비 관점은 `PATHS.batches`(`/batches/:batchId`)를 쓰고 **태블릿은 제 표**를
   *    쓴다(`tabletNav.ts` — `/tablet/batches/:batchId`). 여기서 `PATHS` 를 직접 부르면
   *    태블릿이 kiosk 밖으로 튕겨 나간다.
   * 🔴 **안 넘기면 그 자리는 버튼이 아니라 «글자»로 그려진다**(`BatchStrip`). 눌러도 아무 일도
   *    안 나는 버튼을 두지 않기 위해서다 — 실제로 그 상태로 한동안 나가 있었다(2026-09-21).
   *    태블릿에 **레시피 상세 라우트가 없어서** `onOpenRecipe` 는 실제로 안 넘어간다.
   */
  /** 작업 ID → 그 작업 상세. `batch_id` 는 컨테이너가 붙여 준다 */
  onOpenBatch?: ((batchId: string) => void) | undefined
  /** 시료 처분 대기 안내 「이력에서 처리 →」 — 그 작업 상세 + 처분 창 열림(`dispositionHref`). 없으면 `onOpenBatch` 로 상세만 연다 */
  onOpenDisposition?: ((batchId: string) => void) | undefined
  /** 레시피 이름 → 그 레시피 상세. `recipe_id` 는 컨테이너가 붙여 준다 */
  onOpenRecipe?: ((recipeId: string) => void) | undefined
  /** 대기 상태의 「작업 선택」 → 작업 목록 */
  onSelectBatch?: (() => void) | undefined
  /**
   * 시작 명령이 **서버에 받아들여진 뒤**(202) — 통합 콘솔 셸이 모니터링으로 옮긴다(eric 2026-10-26).
   * 🔴 실패(사전 점검 막힘 · 409 · 403)에는 안 부른다. 작업 줄의 시작 창구(직접 · 경고 확약 · 조치 창 · 시약 확인 · 시뮬 준비)가
   *    전부 `useStartGate` 하나를 지나므로 여기 한 곳이다.
   */
  onStarted?: (() => void) | undefined
  /** 축약형의 「모니터링에서 보기」 → 모니터링. 안 넘기면 링크를 그리지 않는다 */
  onOpenMonitoring?: (() => void) | undefined
  /**
   * 덮어쓰기. **여기서 넘긴 값이 서버 값을 이긴다** — 시험·스토리 용도다.
   * 🔴 실제 화면에서 이 통로로 `allowedCommands` 를 채우지 마라. 그게 낙관적 활성이다.
   */
  strip?: Partial<BatchStripProps> | undefined
}

export function BatchStripContainer({
  eventsOpen,
  onToggleEvents,
  eventsPanel = false,
  eventsOnlyStarted = false,
  eventsInTally = false,
  flowZones = false,
  onNewBatch,
  estopCaption = true,
  tallyEstimate = false,
  nameFirst = false,
  variant = 'full',
  collapsed,
  onToggleCollapsed,
  onOpenBatch,
  onOpenDisposition,
  onOpenRecipe,
  onSelectBatch,
  onOpenMonitoring,
  onStarted,
  strip,
}: BatchStripContainerProps = {}) {
  const permissions = usePermissions()
  const controlDisabled = useControlDisabled()
  const lock = useControlLock()

  /*
   * 🔴 demo 면 요청을 보내지 않고 **fixture 가 서버 자리에 선다**(`useActiveBatchSource`).
   *    「실물을 때렸는데 목이 답했다」를 막으면서, 목 화면에서 줄이 **통째로 비지 않게** 한다 —
   *    종전에는 대타 없이 `skip` 만 해서 demo 작업 줄이 「작업 선택」으로 서 있었고, 같은 문을
   *    쓰는 시료 흐름까지 같이 비었다(2026-09-21).
   */
  const { batches, recipes } = useActiveBatchSource()
  const safety = useSafetyState({ skip: dataSourceFor('safety') !== 'live' })
  /* 걸린 것 목록 — 머리 안전정지 칩이 「E-STOP」 인지 가른다(멈춤 팝업과 같은 쿼리 · 같은 캐시 · 사건이 무효화 · 폴링 없음) */
  const stops = useStops({ skip: dataSourceFor('safety') !== 'live' })

  const acquireLock = useAcquireControlLock()
  const releaseLock = useReleaseControlLock()
  const pauseBatch = usePauseBatch()
  const resumeBatch = useResumeBatch()
  const stopBatch = useStopBatch()
  const recoverBatch = useRecoverBatch()
  const emergencyStop = useEmergencyStop()
  const clearSafety = useClearSafety()
  /*
   * 🔴 **「중지」·「복구 › 폐기」 뒤 모듈 실행 취소**(eric 2026-09-28 · `executionCancel.ts`).
   *    중지는 작업 상태만 바꾸고 모듈은 받은 블록을 끝까지 돈다 — 받아들여진 뒤 취소를 이어 보낸다.
   *    실패 문구·남은 실행 알림은 이 줄의 오류 자리에 서고 **사람이 닫을 때까지** 남는다(DP-02).
   */
  const execCancel = useCancelAfterStop()
  const leftover = useLeftoverExecutions()

  const [confirm, setConfirm] = useState<StripConfirmKind | null>(null)
  // 이벤트 칸의 갈래 — 칸은 펼칠 때만 서므로 여기서 쥔다(접었다 펴도 유지 · URL 아님)
  const [eventsFilter, setEventsFilter] = useState<StripEventsFilter>('all')
  /**
   * 실패 문구 두 자리.
   * 🔴 **훅의 `error` 를 그대로 읽지 않는다.** mutation 의 error 는 다음 발사까지 남아 있어서,
   *    한 번 실패한 문구가 **상시 작업 줄에 계속 붙어 있게** 된다. 명령을 쏠 때마다 비운다.
   */
  const [confirmError, setConfirmError] = useState<string | null>(null)
  const [confirmChecks, setConfirmChecks] = useState<readonly ResumeCheck[]>([])
  const [directError, setDirectError] = useState<string | null>(null)
  /** 시작 전 시약 용량 확인 창(정본 07 §4.4 ③) — 열림 · 거절 문구 · 보충 기록을 보내는 중 */
  const [reagentOpen, setReagentOpen] = useState(false)
  const [reagentError, setReagentError] = useState<string | null>(null)
  const [reagentSending, setReagentSending] = useState(false)
  const replenishReagent = useRecordReagentReplenishment()

  /*
   * 🔴 띄울 한 건은 표면 정책 한 곳(`stripPick.ts`)이 정한다 — 통합 콘솔은 «시작한 작업 → 고른 작업 → 없음»
   *    (eric 2026-10-22 「이걸 선택하기 전에는 대기 이런 게 당연히 나오면 안 돼」), 다른 표면은 종전 «시작한 작업 → 첫 건».
   *    [작업 선택] 드롭다운도 같은 정책으로 선다(`batchPicker`).
   */
  const batchPicker = useExplicitStripPick()
  const chooseBatch = useStripChoiceStore((state) => state.choose)
  const clearChoice = useStripChoiceStore((state) => state.clear)
  const chosenBatchId = useStripChoiceStore((state) => state.chosenBatchId)
  const batch = useStripBatch(batches)
  /*
   * 🔴 **고른 작업 풀기 ×는 «사람이 고른» 시작 전 작업일 때만**(2026-10-26 팀장 결정). 띠가 서버 사실(`started_at`)로 따라가는
   *    시작한 작업이면 풀 선택이 없다 — `pickStripBatch` 가 시작한 작업을 늘 먼저 띄워서, 지워도 화면이 그대로다.
   *    판정은 서버 칸(`started_at`) + 고른 id 대조다. 상태명으로 추론하지 않는다.
   */
  const choiceClearable =
    batchPicker &&
    batch !== undefined &&
    batch.started_at === null &&
    batch.batch_id === chosenBatchId
  const batchId = batch?.batch_id
  /** 「대기」 배지·[시작] 비활성의 «왜» — 프론트 사실과의 대조(`startBlockers.ts`). 활성 근거 아님 */
  const startBlockers = useStartBlockers(batch)
  /*
   * 🔴 **[점검 조치]**(eric 2026-10-23 「사전 점검에서 체크해야 하는 것들을 버튼 형태로」) — 서버가 사전 점검 실패로 `start` 를 빼면
   *    [시작]이 비활성이라 시작 창에 닿을 수 없다. 그래서 그때만 [시작] 옆에 조치 창을 여는 단추를 둔다. 상태변경이 아니라 창을 여는 것이다 —
   *    [시작] 활성은 여전히 `allowed_commands` 그대로다. 점검 결과는 「대기」 사유와 같은 조회(`usePreflightSnapshot` · 같은 캐시 키)다.
   */
  const preflightWaiting =
    batch !== undefined &&
    (batch.status === 'created' || batch.status === 'queued') &&
    !batch.allowed_commands.includes('start')
  const preflightSnapshot = usePreflightSnapshot({
    batchId,
    skip:
      !preflightWaiting ||
      !(permissions?.includes('control') ?? false) ||
      controlDisabled ||
      dataSourceFor('batches') !== 'live',
  })
  /** 연 때의 작업 · 점검 결과를 붙잡는다 — 자리 정리로 그 작업이 줄에서 빠져도 창은 결과를 보인다 */
  const [fixesFor, setFixesFor] = useState<{ batchId: string; result: PreflightResult } | null>(
    null,
  )
  const fixesResult =
    preflightWaiting && preflightSnapshot.data !== undefined && !preflightSnapshot.data.passed
      ? preflightSnapshot.data
      : undefined

  /**
   * 🔴 **진행은 이벤트 채널 하나에서만 온다** — 그래서 `controlDisabled`(둘 중 하나라도 stale)이
   *    아니라 **이벤트 채널의 stale** 로 가린다. 안전 채널이 조용하다고 멀쩡한 진행을 지우면
   *    화면이 모르는 척을 한다. 반대로 이벤트가 끊겼으면 마지막 값은 **더 이상 「지금」이 아니다.**
   */
  const eventsStale = useStreamState('events').stale
  const safetyStale = useStreamState('safety').stale
  /* 머리 안전정지 칩의 「E-STOP」 — 멈춤 팝업과 같은 판정(`estopMode.ts`) · 안전 구독이 끊겼으면 모른다 */
  const globalEstop =
    !safetyStale && batch !== undefined && isGlobalEstop(systemSafetyStopOf(stops.data), [batch])
  /*
   * 🔴 **스냅샷 1회로 진행 보관소의 빈 자리를 채운다**(쵸단 실측 2026-10-06 — 실행 도중 화면을 열면 첫 `module_status` 가 오기
   *    전까지 지금 도는 장비까지 「대기 · 전체 수량 집계 중」이었다). `GET /modules` 의 `current_execution` 이 그 재료다.
   *    ⚠ 새 조회가 아니다 — `useLeftoverExecutions` 가 같은 조건(live)으로 이미 부르는 목록과 같은 캐시다(DP-01).
   *    구독이 이미 말한 모듈은 건너뛴다(`seedStreamProgress`).
   */
  const seedModules = useModules({ skip: dataSourceFor('modules') !== 'live' })
  /* 받은 시각(`dataUpdatedAt`)을 같이 넘긴다 — 재연결 뒤 끊기기 전 캐시로 채우지 않게(`seedAllowed`) */
  useStreamProgressSeed(
    seedModules.data?.items,
    eventsStale,
    seedModules.data === undefined ? undefined : seedModules.dataUpdatedAt,
  )
  const progress = useBatchProgress(batchId)
  const runningModuleType = useBatchRunningModuleType(batchId)

  /*
   * 🔴 **들은 모듈별 스텝 수를 기억하고, 새로 고친 뒤 되살린다**(eric 2026-09-28 · `stripStepMemory.ts`).
   *    - 읽기: 배치가 바뀔 때 한 번. 새로 고친 직후 미러 첫 프레임(≤1초)이 오기 전에는 기억으로 선다
   *    - 쓰기: **구독이 준 값만** 적는다(기억·예상을 되적지 않는다). 실제 값이 오면 그것이 이기고 저장도 고쳐진다
   *    - stale 이면 기억도 안 넘긴다 — 진행 표시를 접는 규약 그대로다
   *    새 조회가 없다(DP-01) — 이미 오는 구독 미러와 브라우저 저장소뿐이다.
   */
  const recipeId = batch?.recipe_id
  const recipeVersion = batch?.recipe_version
  const sampleCount = batch?.sample_count
  const memory = useMemo(
    () =>
      batchId === undefined || recipeId === undefined
        ? EMPTY_STEP_MEMORY
        : readStepMemory({
            batch_id: batchId,
            recipe_id: recipeId,
            recipe_version: recipeVersion as number,
            sample_count: sampleCount as number,
          }),
    [batchId, recipeId, recipeVersion, sampleCount],
  )
  useEffect(() => {
    if (batchId === undefined || recipeId === undefined || eventsStale) return
    rememberSteps(
      {
        batch_id: batchId,
        recipe_id: recipeId,
        recipe_version: recipeVersion as number,
        sample_count: sampleCount as number,
      },
      stripModuleSteps(progress),
    )
  }, [batchId, recipeId, recipeVersion, sampleCount, progress, eventsStale])

  /**
   * 🔴 **요청이 떠 있는 동안 같은 줄의 명령을 다시 보내지 못하게 한다.** 멱등키(`command_id`)는
   *    요청마다 새로 나므로 두 번 누르면 **두 개의 진짜 명령**이 된다.
   *    ⚠ E-STOP 은 여기 들어가지 않는다 — 버튼은 계속 활성이고 중복만 처리기가 막는다.
   */
  const commandInFlight =
    acquireLock.isPending ||
    releaseLock.isPending ||
    pauseBatch.isPending ||
    resumeBatch.isPending ||
    stopBatch.isPending ||
    recoverBatch.isPending ||
    execCancel.pending

  /**
   * 🔴 **[시작]은 «사전 점검을 먼저 지난 뒤» 나간다**(2026-09-22). 서버가 Pre-flight 경고를
   *    확약하지 않은 시작을 409 로 거부한다(`context.reason = warn_ack_required`) —
   *    종전처럼 `command_id` 만 보내면 **눌러도 안 나간다.**
   * 🔴 **흐름은 `useStartGate` 한 곳에 있다.** 다른 시작 창구(개발용 「바로 돌리기」 등)도
   *    같은 훅을 쓴다 — 갈라 지으면 한쪽만 확약을 싣는다.
   */
  const start = useStartGate({
    batchId,
    blocked: commandInFlight,
    onError: setDirectError,
    onSend: () => {
      setDirectError(null)
    },
    ...(onStarted === undefined ? {} : { onStarted }),
  })
  /*
   * 🔴 **시뮬 준비**(개발계 · 「테스트 작업 시작」 플래그가 켜진 환경에서만 · eric 2026-10-14) — [시작]이 사전 점검 «앞»에서 자동 준비를
   *    돌린다(안전 해제 · 이송 자리 정리 · 모듈 초기화 · 소모품 · 폐기통). 이 작업은 남긴다. 실장비 · 고객 화면에서는 꺼져 있다.
   */
  const sim = useSimStartPrepare({ batchId, recipeId })
  /** 🔴 사전 점검도 [시작]의 «일부»다 — 도는 동안 다시 누르면 점검이 두 번 돈다. 시약 보충 기록 · 시뮬 준비도 같다 */
  const startInFlight = start.pending || reagentSending || sim.preparing
  const busy = commandInFlight || startInFlight

  /** [시작] — 시뮬이면 준비를 먼저 돌리고, 멈추면 그 사유를 작업 줄에 두고 시작하지 않는다 */
  const beginStart = async () => {
    if (sim.enabled) {
      setDirectError(null)
      const failed = await sim.prepare()
      if (failed !== undefined) {
        setDirectError(failed)
        return
      }
    }
    start.begin()
  }

  /**
   * **시작 전 시약 용량 확인 → 시작**(정본 07 §4.4 ④ 「시약 보충 기록 → Pre-flight 다시 → 시작」).
   * 🔴 본문은 차례대로 하나씩 보낸다 — 하나라도 거절되면 거기서 멈추고 창 안에 그 까닭을 둔다. **앞서 보낸 기록은 되돌리지 않는다**
   *    (사람이 실제로 채웠다는 사실 기록이다). 다시 누르면 같은 본문을 다시 보낸다 — 보충은 «맞춤»이라 같은 값을 두 번 맞춰도 같다.
   * 🔴 대상은 서버 칸 `reagent_module_id` 하나다. 창이 `null` 이면 확정 버튼이 비활성이라 여기까지 오지 않는다.
   * ⚠ 다 보내면 창을 닫고 종전 시작 흐름(`useStartGate.begin` — 사전 점검 → 차단 · 경고 확약 창 → 시작)으로 잇는다.
   */
  const confirmReagents = async (bodies: readonly ReagentBody[]) => {
    const moduleId = batch?.reagent_module_id
    if (batchId === undefined || moduleId == null || busy) return
    setReagentError(null)
    setReagentSending(true)
    try {
      for (const body of bodies) {
        await replenishReagent.mutateAsync({
          moduleId,
          labwareId: body.labwareId,
          wellNames: body.wellNames,
          volume: body.volume,
          note: serverText('strip.reagentNote', { batch: batchId }),
        })
      }
    } catch (error) {
      setReagentError(errorMessage(error as ApiError))
      return
    } finally {
      setReagentSending(false)
    }
    setReagentOpen(false)
    start.begin()
  }

  /**
   * 「00 사전 점검」 칸(eric 2026-10-29) — [시작]을 누른 뒤 점검 창이 열린 동안 강조. 🔴 재료는 서버 사실(`started_at` · 점검 결과
   * `passed`)과 이 컨테이너가 연 창(UI 상태)뿐이다(`stripPreflightPhase`). 시약 용량 확인 창 · 시뮬 준비도 [시작]의 점검 앞 단계라 「점검 중」.
   * 경고 확약 얼굴인데 결과가 없으면(시작 거절이 정정해 준 목록) 그 앞 점검이 통과였다 — 경고는 통과한 회차에만 실린다.
   */
  const preflightPhase = stripPreflightPhase({
    started: batch?.started_at != null,
    checking: reagentOpen || reagentSending || sim.preparing || start.checking,
    result:
      start.gate !== null
        ? start.gate.phase === 'warn'
          ? (start.gate.result ?? { passed: true })
          : start.gate.result
        : fixesFor?.result,
    starting: start.starting,
  })

  /** 대화를 거치지 않는 명령(획득 · 반납 · 시작 · 일시정지 · 재개)의 실패 처리 */
  const direct = {
    onError: (error: ApiError) => setDirectError(errorMessage(error)),
    onSuccess: () => setDirectError(null),
  }

  const send = (command: Extract<BatchCommand, 'pause' | 'resume'>) => {
    if (batchId === undefined || busy) return
    setDirectError(null)
    const vars = { batchId }
    if (command === 'pause') pauseBatch.mutate(vars, direct)
    else resumeBatch.mutate(vars, direct)
  }

  /*
   * 끊긴 시간 원자 방식을 고를 수 있으면(`resume_timed_atom_choices` 가 둘) [재개]가 고르기 대화를 연다 — 그 밖은 종전대로 바로 나간다.
   * 🔴 고를 것이 하나뿐이면 묻지 않는다(계약 「화면은 둘일 때만 칸을 보인다」 · 백엔드 0.28.15).
   */
  const timedAtomChoices = timedAtomChoicesOf(batch)

  /** 확인 대화를 연다 — 이전 실패 문구가 새 대화로 넘어가지 않게 비운다 */
  const open = (kind: StripConfirmKind) => {
    setConfirmError(null)
    setConfirmChecks([])
    setConfirm(kind)
  }

  const onCommand = (command: BatchCommand) => {
    // 🔴 되돌릴 수 없는 둘만 확인 대화를 연다. 나머지는 바로 나간다
    if (command === 'stop') open('stop')
    else if (command === 'recover') open('recover')
    else if (command === 'start') {
      /*
       * 🔴 레시피 판에 시약이 있으면 시약 용량 확인 창을 먼저 연다 — 없으면 종전대로 사전 점검으로 바로 간다.
       *    시뮬 준비가 켜졌으면 그 준비가 시약까지 채우므로 창 없이 준비 → 사전 점검으로 간다.
       */
      if (sim.enabled) void beginStart()
      else if (needsReagentConfirm(batch)) {
        setReagentError(null)
        setReagentOpen(true)
      } else void beginStart()
    } else if (command === 'resume' && timedAtomChoices.length >= 2) open('resume')
    else if (command === 'pause' || command === 'resume') send(command)
    // ⚠ `load_confirm`·`unload_confirm` 은 **`Batch.allowed_commands` 에 없다**(계약 enum 5종).
    //   적재·수거 확약은 `POST /loads|unloads/{id}/confirm` 이고 활성 근거도 다르다
    //   (`TrayLoad.confirmed_at === null`) — 두 축을 여기서 섞지 않는다.
  }

  const onLockAction = (action: ControlLockAction) => {
    if (busy) return
    // 남의 락을 끊는 강제 해제만 확인 창(사유 입력)을 연다
    if (action === 'force') {
      open('force')
      return
    }
    setDirectError(null)
    /*
     * 🔴 내 락 반납은 **묻지 않고 바로 나간다**(eric 2026-10-29 「그냥 반납하면 안 돼? 안 물어보고?」).
     *    `DELETE /control-token` 본문이 `IrreversibleCommandEnvelope` 라 `reason` 이 필수여서 고정 문구를 싣는다.
     *    실패는 다른 바로 나가는 명령과 같은 3행 오류 자리(`direct`)에 뜬다.
     */
    if (action === 'release')
      releaseLock.mutate({ reason: serverText('lock.releaseReason') }, direct)
    else acquireLock.mutate({}, direct)
  }

  const onConfirm = (action: StripCommandAction) => {
    if (busy) return
    setConfirmError(null)
    const handlers = {
      onError: (error: ApiError) => {
        setConfirmError(errorMessage(error))
        setConfirmChecks((checks) => [...new Set([...checks, ...refusedResumeChecks(error)])])
      },
      onSuccess: () => setConfirm(null),
    }
    if (action.kind === 'stop') {
      if (batchId === undefined) return
      stopBatch.mutate(
        { batchId, reason: action.reason },
        {
          ...handlers,
          // 🔴 **받아들여진 «뒤»에만** 취소를 보낸다. 중지가 거절되면 모듈을 건드리지 않는다
          onSuccess: () => {
            setConfirm(null)
            void execCancel.cancelAfter(batchId, action.reason)
          },
        },
      )
    } else if (action.kind === 'resume') {
      if (batchId === undefined) return
      resumeBatch.mutate(
        { batchId, ...(action.timedAtom === undefined ? {} : { timedAtom: action.timedAtom }) },
        handlers,
      )
    } else if (action.kind === 'recover') {
      if (batchId === undefined) return
      const { decision } = action
      recoverBatch.mutate(
        {
          batchId,
          decision,
          ...(action.reason === undefined ? {} : { reason: action.reason }),
          // 확인 재개(`rerun_cut_atom`) — 현장 확인 두 칸 · 고른 시간 원자 방식(대화가 채운 것만)
          ...(action.physicalChecked === undefined
            ? {}
            : { physicalChecked: action.physicalChecked }),
          ...(action.physicalChecks === undefined ? {} : { physicalChecks: action.physicalChecks }),
          ...(action.timedAtom === undefined ? {} : { timedAtom: action.timedAtom }),
        },
        {
          ...handlers,
          // 🔴 **폐기만** 작업을 끝낸다(`recover_discard` → `aborted`). 재개·완료 확정은 모듈을 건드리지 않는다
          onSuccess: () => {
            setConfirm(null)
            if (decision === 'discard') void execCancel.cancelAfter(batchId, action.reason)
          },
        },
      )
    } else {
      releaseLock.mutate(
        { reason: action.reason, ...(action.force ? { force: true } : {}) },
        handlers,
      )
    }
  }

  /**
   * 3행 오류 자리에 설 **한 줄** — 우선순위: ① 방금 누른 명령의 실패 ② 실행 취소 실패(+[다시 시도])
   * ③ 끝난 작업의 남은 실행(+[실행 취소]). 🔴 셋 다 **사람이 닫을 때까지** 남는다(DP-02).
   * 🔴 ③ 은 **이벤트가 끊기면 세우지 않는다** — 스냅샷이 «지금»이 아니다. 버튼은 어차피 전부 비활성이다.
   * 🔴 ③ 의 [실행 취소]는 **사람이 누를 때만** 나간다. 자동으로 쏘지 않는다.
   */
  const cancelLeftover = async () => {
    for (const id of leftover.batchIds) {
      await execCancel.cancelAfter(id, serverText('strip.execCancel.leftoverReason'))
    }
  }
  const notice: {
    text: string
    dismiss: () => void
    action?: { label: string; onClick: () => void }
  } | null =
    directError !== null
      ? { text: directError, dismiss: () => setDirectError(null) }
      : execCancel.failureText !== null
        ? {
            text: execCancel.failureText,
            dismiss: execCancel.dismiss,
            action: { label: t('action.retryCancelExecution'), onClick: execCancel.retry },
          }
        : leftover.text !== null && !eventsStale
          ? {
              text: leftover.text,
              dismiss: leftover.dismiss,
              action: { label: t('action.cancelExecution'), onClick: () => void cancelLeftover() },
            }
          : null

  const me = useCurrentUser()
  /*
   * 「지금」 — 경과의 왼쪽 항. 분 단위로 줄을 다시 그리는 것은 진행바의 읽기 값(`valueText`)용이고,
   * **초는 시계 조각(`StripClock`)이 따로 센다**(eric 2026-09-28) — 여기를 1초로 돌리면 줄 전체가 매초 돈다.
   * 서버에 묻는 것이 없다(DP-01 무관).
   */
  const now = useMinuteTick()
  const myName = [me?.name, me?.username].find((v) => typeof v === 'string' && v.trim() !== '')

  /*
   * 1행 「담당」 — 풀 수 있는 이름은 «나» 하나다(`stripOwnerText`). 남이 만든 작업은 칸이 안 선다.
   * 🔴 여기서 `GET /users` 를 부르지 않는다 — admin 전용이고(operator · viewer 403) 작업 줄은 모든 역할이 본다.
   */
  const ownerText = stripOwnerText(batch, me)
  /*
   * 준비 · 완료 영역의 재료(통합 콘솔 `flowZones` 만) — **이 배치의 실행을 쥔 모듈이 사람 적재 대기인가**. 위 `seedModules`
   * (`GET /modules` · 구독이 갱신하는 같은 캐시)의 `state` · `current_execution.batch_id` 를 읽는다. 새 조회가 아니다(DP-01).
   */
  const loadWait = flowZones ? stripLoadWait(seedModules.data?.items, batchId) : undefined
  /*
   * 6축 이송 중(통합 콘솔 `flowZones` 만) — **이 배치 추적의 트레이가 `robot` · `in_transit`**(모니터링 시료 카드와 같은 근거 ·
   * `batchTransferring` · 서버 v0.9.4 — 자원 점유가 걷혔다).
   * 🔴 폴링이 아니다(DP-01) — 같은 캐시를 서버의 `batch_tracking` 구독 프레임이 다시 읽힌다(`stream/bridge.ts`). 끊겼거나 도는 작업이
   *    아니면 부르지 않는다. 실패면 배지만 「준비 중」으로 남는다(부가 조회).
   */
  const inFlightBatch = batch?.status === 'running' || batch?.status === 'paused'
  const tracking = useBatchTracking({
    batchId,
    skip: dataSourceFor('batches') !== 'live' || !flowZones || eventsStale || !inFlightBatch,
  })
  const transferring =
    flowZones && !eventsStale && tracking.isSuccess && batchTransferring(tracking.data, batchId)

  /*
   * 시료 처분 대기 안내(통합 콘솔 `flowZones` 만 · 빈 띠일 때만 · `stripDisposition`) — [중지]한 작업은 띠에서 바로 빠지지만
   * 시료 처분은 사람이 닫을 때까지 남는다(DP-02). 서버 필터 `status=aborted&review=pending` 의 맨 앞 한 건과 총수만 받는다.
   * 🔴 폴링이 아니다(DP-01) — 구독 `batch_status` 가 `keys.batches` 를 무효화하면 다시 온다(서버가 처분 대기를 풀면 빠진다).
   *    끊겼으면 싣지 않는다(마지막 값을 «지금»으로 보이지 않는다). 실패 · viewer 는 안내만 안 선다(부가 조회).
   * ⚠ × 는 이 화면(셸이 살아 있는 동안)에서만 숨긴다 — 서버의 처분 대기는 그대로다. 새로 고치면 다시 선다.
   */
  const [dismissedDisposition, setDismissedDisposition] = useState<string | undefined>()
  const dispositionList = useBatches({
    status: 'aborted',
    review: 'pending',
    limit: 1,
    skip: dataSourceFor('batches') !== 'live' || !flowZones || batch !== undefined,
  })
  const disposition =
    !flowZones || batch !== undefined || eventsStale || !dispositionList.isSuccess
      ? undefined
      : stripDisposition(dispositionList.data, dismissedDisposition)

  /*
   * 요약 카드 「예상」(통합 콘솔 `tallyEstimate` 만 · eric 2026-10-06 「오른쪽 전체 경과 영역에.. 예상 시간도 넣어줘」).
   * 🔴 **배치 단위 예상 종료 · 남은 시간이 서버에 없다**(openapi 전수 · `GET /batches/{id}` 키 19개 · 진행 프레임 · preflight 확인 —
   *    `docs/backend-requests.csv` BR-09). 그래서 줄만 세우고 「미수신」 + 까닭이다. 경과 ÷ 진행 비율로 만들지 않는다(분모에 `+?`).
   * ⚠ `POST /recipes/{id}/simulate` 의 `estimated_duration_sec` 은 쓰지 않는다(팀장 결정 2026-10-06) — 레시피 «최신 판»의 선언 시간 합
   *    (서버 스스로 「하한」)이고 dev2 sim 에서 08:03:20 ↔ 실제 1:26~1:29 로 어긋난다. eric 이 2026-09-28 에 같은 값을 걷은 이력도 있다.
   */
  /* 작업이 없을 때도 넘긴다 — 빈 요약 카드가 「시간 – / –」 줄 구성을 작업 있을 때와 맞춘다(eric 2026-10-29) */
  const estimate = !tallyEstimate ? undefined : { reason: t('strip.estimate.notSent') }
  const stripBatch =
    batch === undefined
      ? null
      : eventsStale
        ? toStripBatch(batch, stripRecipeNames(recipes), undefined, now)
        : toStripBatch(batch, stripRecipeNames(recipes), progress, now, {
            memory,
            runningModuleType,
            ...(flowZones
              ? {
                  flow: {
                    loadWait,
                    transferring,
                    transferRoutes: tracking.isSuccess
                      ? stripTransferRoutes(tracking.data, batchId, seedModules.data?.items)
                      : [],
                    /* 격리 · 낙하 트레이 — 떠나기 전 모듈 칸에 표지(`stripTrayHolds` · 같은 추적 응답 · 새 요청 아님) */
                    trayHolds: tracking.isSuccess
                      ? stripTrayHolds(tracking.data, batchId, seedModules.data?.items)
                      : [],
                    /* 멈춘 모듈 — 일시정지 작업의 `halt_module_id` 를 모듈 목록으로 공정 키에(`stripHaltAt` · 새 요청 아님) */
                    haltAt: stripHaltAt(batch, seedModules.data?.items),
                  },
                }
              : {}),
          })

  /*
   * 멈춤 한 줄(정본 07 §4.12 · `haltLine`) — 서버 `resume_headline`을 우선 표시하고 없으면 `halt_resume` · `resume_blockers`에서 고른다. 스냅샷 + 구독 `batch_status`
   * (같은 값이 실린다 · `stream/bridge.ts` 가 배치를 다시 받는다)로 갱신된다. 🔴 끊겼으면 싣지 않는다 — 「오류가 풀렸습니다」를
   * 마지막 값으로 «지금»인 척 두지 않는다(버튼은 어차피 stale 로 전부 비활성이다).
   */
  const halt = eventsStale ? undefined : haltLine(batch)
  const stripProps: BatchStripProps = {
    batch:
      stripBatch === null
        ? null
        : {
            ...stripBatch,
            ...(ownerText === undefined ? {} : { ownerText }),
            ...(halt === undefined ? {} : { halt }),
          },
    // 🔴 서버가 준 집합 **그대로**. 작업이 없으면 빈 배열 = 「지금 할 수 있는 것이 없다」
    allowedCommands: batch?.allowed_commands ?? [],
    /*
     * 🔴 내 락의 이름은 **내 계정**이다(표시 이름, 없으면 로그인 ID). 서버 `holder` 는 `user_id`(UUID)라
     *    사람이 못 읽는다 — eric 2026-09-22 「획득 부분에서 uuid 말고 해당 유저 계정으로」.
     */
    lock: toControlLockView(lock, myName),
    permissions,
    safety: {
      state: safety.data?.state ?? 'unknown',
      stopReach: safety.isError || safetyStale ? null : safety.data?.stop_reach,
      /* 전체 E-STOP — 멈춤 팝업과 같은 판정(`isGlobalEstop` · 걸린 것 목록 + 이 작업의 `halt_code`) · 끊겼으면 모른다 */
      ...(globalEstop ? { estop: true as const } : {}),
    },
    controlDisabled,
    commandInFlight: busy,
    startInFlight,
    startBlockers,
    ...(notice === null ? {} : { commandError: notice.text }),
    ...(notice?.action === undefined ? {} : { commandErrorAction: notice.action }),
    onCommand,
    onLockAction,
    ...(fixesResult === undefined || batchId === undefined
      ? {}
      : { onPreflightFixes: () => setFixesFor({ batchId, result: fixesResult }) }),
    // 🔴 실패 문구는 **사람이 닫을 때** 사라진다(또는 다음 명령을 쏠 때). 저절로 지워지면
    //    그 자리에 단계 문구가 돌아오면서 「실패가 해소됐다」로 읽힌다(DP-02).
    onDismissError: () => {
      notice?.dismiss()
    },
    // 🔴 확인 대화 없음 · 역할/제어락/상태/stale 무관 항상 활성(정본 07 §2.7).
    //    막는 것은 **중복 발사 하나**다 — 버튼을 비활성으로 만들지 않는다.
    onEmergencyStop: () => {
      if (!emergencyStop.isPending) emergencyStop.mutate({})
    },
    onClearSafety: () => {
      if (!clearSafety.isPending) clearSafety.mutate({})
    },
    ...(eventsOpen === undefined ? {} : { eventsOpen }),
    ...(onToggleEvents === undefined ? {} : { onToggleEvents }),
    eventsOnlyStarted,
    eventsInTally,
    flowZones,
    ...(flowZones ? { preflight: preflightPhase } : {}),
    ...(choiceClearable ? { onClearChoice: clearChoice } : {}),
    ...(batchPicker
      ? {
          batchChoice: {
            ...stripBatchChoice(batches, batch, chooseBatch, onNewBatch),
            /* 🔴 [작업 추가]는 `control` 이 없으면 숨기지 않고 비활성 + 사유(viewer) — 고르기 자체는 화면 선택이라 누구나 한다 */
            ...(permissions.includes('control')
              ? {}
              : { addDisabledReason: t('reason.noPermission', { perm: 'control' }) }),
          },
        }
      : {}),
    estopCaption,
    ...(estimate === undefined ? {} : { estimate }),
    nameFirst,
    variant,
    ...(collapsed === undefined ? {} : { collapsed }),
    ...(onToggleCollapsed === undefined ? {} : { onToggleCollapsed }),
    // 🔴 펼쳤을 때만 선다 — 접힌 동안 원장을 묻지 않는다(펼칠 때 스냅샷 1회)
    ...(eventsPanel && eventsOpen === true
      ? {
          events: (
            <StripEvents
              batchId={batchId}
              filter={eventsFilter}
              /* 열린 칸이라 토글이 곧 닫기다(접는 움직임 포함 · `useEventsFold`) */
              {...(onToggleEvents === undefined ? {} : { onClose: onToggleEvents })}
              onFilterChange={setEventsFilter}
            />
          ),
        }
      : {}),
    /*
     * 🔴 **셸이 준 이동에 «서버가 준 id» 를 붙이는 자리.** 경로는 셸이 알고 id 는 여기가 안다.
     * 🔴 **작업이 없으면 넘기지 않는다** — 열 대상이 없는데 버튼만 사는 것을 막는다.
     *    `BatchStrip` 이 그 자리를 글자로 그린다(죽은 버튼 금지).
     * ⚠ 레시피는 **`batch.recipe_id`** 로 간다. 화면에 뜨는 것은 `recipeName` 이지만 그건
     *   `/recipes` 를 따로 받아 붙인 «표시»라 이동의 근거가 못 된다(이름이 없으면 id 가 남는다).
     */
    ...(onOpenBatch === undefined || batchId === undefined
      ? {}
      : { onOpenBatch: () => onOpenBatch(batchId) }),
    ...(disposition === undefined
      ? {}
      : {
          disposition: {
            shortId: disposition.shortId,
            more: disposition.more,
            ...(onOpenDisposition !== undefined
              ? { onOpen: () => onOpenDisposition(disposition.batchId) }
              : onOpenBatch === undefined
                ? {}
                : { onOpen: () => onOpenBatch(disposition.batchId) }),
            onDismiss: () => setDismissedDisposition(disposition.batchId),
          },
        }),
    ...(onOpenRecipe === undefined || batch === undefined
      ? {}
      : { onOpenRecipe: () => onOpenRecipe(batch.recipe_id) }),
    ...(onSelectBatch === undefined ? {} : { onSelectBatch }),
    ...(onOpenMonitoring === undefined ? {} : { onOpenMonitoring }),
    ...strip,
  }

  /*
   * 🔴 **펼친 채로 작업이 줄에서 빠지면(또는 시작 전 작업만 남으면) 접는다**(eric 2026-09-28).
   *    단추는 «시작된» 작업이 있을 때만 서는데(`BatchStrip` · `batch.started`), 펼침 상태가 남으면
   *    단추 없이 본문이 가려진 채로 선다(콘솔 셸은 펼친 동안 본문을 `inert` 로 가린다).
   *    셸이 준 토글을 그대로 부른다(접힘 움직임 포함 · 접는 중 재호출은 `useEventsFold` 가 무시한다).
   */
  const eventsAvailable =
    !(stripProps.eventsOnlyStarted ?? false) || stripProps.batch?.started === true
  useEffect(() => {
    if (!eventsAvailable && eventsOpen === true) onToggleEvents?.()
  }, [eventsAvailable, eventsOpen, onToggleEvents])

  return (
    <>
      <BatchStrip {...stripProps} />
      {fixesFor === null ? null : (
        /* 차단 얼굴 그대로 — 확정 단추가 없고 줄마다 조치 단추 · [사전 점검 다시]가 선다(`StartPreflightActions.tsx`) */
        <StripStartDialog
          access={{ permissions, controlDisabled, lockHeld: lock.held }}
          batchId={fixesFor.batchId}
          presentation={flowZones ? 'grouped' : 'list'}
          fixable
          gate={{ phase: 'blocked', result: fixesFor.result }}
          onCancel={() => setFixesFor(null)}
          onConfirm={() => setFixesFor(null)}
          /* 통과하면 이 창을 닫고 시작 흐름의 창(경고 확약 → [시작])으로 잇는다 — 다시 만든 작업이면 그 작업으로 */
          onPassed={(result, passedBatchId) => {
            setFixesFor(null)
            start.adopt(result, passedBatchId)
          }}
        />
      )}
      {!reagentOpen || batch === undefined ? null : (
        /* `key` — 다른 작업으로 바뀌면 칸(그 배치의 표준 충전량)을 새로 세운다 */
        <StripReagentDialog
          access={{ permissions, controlDisabled, lockHeld: lock.held }}
          basis={batch.reagent_basis}
          batchId={batch.batch_id}
          {...(reagentError === null ? {} : { errorMessage: reagentError })}
          key={batch.batch_id}
          moduleId={batch.reagent_module_id}
          onCancel={() => {
            setReagentOpen(false)
          }}
          onConfirm={(bodies) => {
            void confirmReagents(bodies)
          }}
          pending={busy}
        />
      )}
      {start.gate === null ? null : (
        /*
         * 🔴 **`key` 가 목록을 잠근다.** 목록이 갈리면 컴포넌트가 다시 서면서 확약 체크가
         *    풀린다 — 한 목록에서 받은 확인이 다른 목록으로 넘어가면 그것이 곧
         *    「다른 것을 보고 확약한 것」이고, 서버가 `warn_ack_mismatch` 로 거절할 바로 그 일이다.
         */
        <StripStartDialog
          access={{ permissions, controlDisabled, lockHeld: lock.held }}
          {...(batchId === undefined ? {} : { batchId })}
          {...(directError === null ? {} : { errorMessage: directError })}
          /* 차단 얼굴에 줄마다 조치 단추(eric 2026-10-23 「사전 점검에서 체크해야 하는 것들을 버튼 형태로」) */
          fixable
          gate={start.gate}
          presentation={flowZones ? 'grouped' : 'list'}
          key={startGateKey(start.gate)}
          onCancel={start.close}
          onConfirm={start.confirm}
          /* 🔴 같은 창의 다음 얼굴 — 조치 칸이 통과시킨 결과(다시 만든 작업이면 그 작업)로 경고 확약 → [시작] */
          onPassed={start.adopt}
          pending={busy}
        />
      )}
      {confirm === null ? null : (
        <StripCommandDialog
          key={JSON.stringify([
            batchId,
            confirm,
            batch?.version,
            batch?.resume_route,
            batch?.resume_checks,
            timedAtomChoices,
          ])}
          access={{ permissions, controlDisabled, lockHeld: lock.held }}
          allowedCommands={batch?.allowed_commands}
          batchStatus={batch?.status}
          estopped={safety.data?.state === 'emergency_stop'}
          kind={confirm}
          {...(batchId === undefined ||
          (confirm !== 'stop' && confirm !== 'recover' && confirm !== 'resume')
            ? {}
            : { batchId })}
          {...(confirmError === null ? {} : { errorMessage: confirmError })}
          {...(batch?.resume_route == null ? {} : { resumeRoute: batch.resume_route })}
          resumeChecks={[...new Set([...(batch?.resume_checks ?? []), ...confirmChecks])]}
          timedAtomChoices={timedAtomChoices}
          onCancel={() => setConfirm(null)}
          onSubmit={onConfirm}
          pending={busy}
        />
      )}
    </>
  )
}
